import { IconMagnifyingGlass } from "central-icons/IconMagnifyingGlass";
import { Command as CommandPrimitive } from "cmdk";
import type * as React from "react";

import { cn } from "@/lib/utils";

const COMMAND_INPUT_HEIGHT = 44;
const COMMAND_INPUT_BORDER_HEIGHT = 1;
const COMMAND_VERTICAL_MARGIN_HEIGHT = 8;
const COMMAND_LIST_HEIGHT = `calc(var(--radix-popover-content-available-height) - ${COMMAND_INPUT_HEIGHT + COMMAND_INPUT_BORDER_HEIGHT + COMMAND_VERTICAL_MARGIN_HEIGHT}px)`;

const Command = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) => (
  <CommandPrimitive
    className={cn(
      "flex size-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
      className
    )}
    {...props}
  />
);

const CommandInput = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) => (
  <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
    <IconMagnifyingGlass
      aria-hidden="true"
      className="mr-2 size-4 shrink-0 opacity-50"
      focusable="false"
    />
    <CommandPrimitive.Input
      className={cn(
        "flex w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      style={{ height: COMMAND_INPUT_HEIGHT }}
      {...props}
    />
  </div>
);

const CommandList = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) => (
  <CommandPrimitive.List
    className={cn("overflow-y-auto overflow-x-hidden", className)}
    style={{ maxHeight: COMMAND_LIST_HEIGHT }}
    {...props}
  />
);

const CommandEmpty = (
  props: React.ComponentProps<typeof CommandPrimitive.Empty>
) => <CommandPrimitive.Empty className="py-6 text-center text-sm" {...props} />;

const CommandGroup = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) => (
  <CommandPrimitive.Group
    className={cn(
      "overflow-y-auto overflow-x-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:text-xs",
      className
    )}
    {...props}
  />
);

const CommandSeparator = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) => (
  <CommandPrimitive.Separator
    className={cn("-mx-1 h-px bg-border", className)}
    {...props}
  />
);

const CommandItem = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) => (
  <CommandPrimitive.Item
    className={cn(
      "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      className
    )}
    {...props}
  />
);

export {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
};
